import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_URI } from "../utils";

/**
 * Custom hook for handling user signup
 * @param {Object} options - Configuration options
 * @param {Function} options.toast - Toast notification function
 * @param {Function} options.toggle - Toggle function for loading state
 * @returns {Object} - Mutation object with signup functionality
 */
export const useSignUp = (toast, toggle) => {
  return useMutation({
    mutationFn: async (formData) => {
      try {
        toggle();
        console.log('Sending signup request with data:', formData);
        
        const { data } = await axios.post(`${API_URI}/auth/register`, formData, {
          headers: {
            'Content-Type': 'application/json',
          },
          validateStatus: (status) => status < 500, // Don't throw for 4xx errors
        });
        
        console.log('Signup successful, response:', data);
        return data;
      } catch (error) {
        console.error('Signup error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            data: error.config?.data,
          },
        });
        throw error; // Re-throw to trigger onError
      }
    },
    onError: (error) => {
      console.error('Signup mutation error:', error);
      toggle();
      
      const errorMessage = error?.response?.data?.message || 
                         error.message || 
                         'Failed to create account. Please try again.';
      
      toast.error(errorMessage);
      
      // Handle specific error cases
      if (error.response) {
        // Handle 4xx/5xx responses
        console.error('Server responded with error:', {
          status: error.response.status,
          data: error.response.data,
        });
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received from server');
        toast.error('Unable to connect to server. Please check your connection.');
      }
    },
    onSuccess: (data) => {
      try {
        console.log('Signup successful, processing response:', data);
        
        // Check if we have valid response data
        if (!data) {
          throw new Error('No response data received from server');
        }
        
        // If there's an error message in the response, treat it as an error
        if (data.error || data.message?.toLowerCase().includes('error')) {
          throw new Error(data.message || 'An error occurred during signup');
        }
        
        // Check for required user data
        if (!data.user?._id) {
          console.error('Invalid user data in response:', data);
          throw new Error(
            data.message || 'Server response is missing required user information. Please contact support.'
          );
        }
        
        // Store OTP data for verification
        localStorage.setItem(
          "otp_data",
          JSON.stringify({
            otpLevel: true,
            id: data.user._id,
            email: data.user.email, // Store email for verification page
          })
        );
        
        toast.success(data.message || 'Account created successfully! Please check your email for verification.');
        
        // Redirect after a short delay
        setTimeout(() => {
          window.location.replace("/otp-verification");
        }, 2000);
        
      } catch (error) {
        console.error('Error processing signup success:', error);
        const errorMessage = error.message || 'An error occurred while processing your signup. Please try again.';
        toast.error(errorMessage);
        
        // If we have data but no user, log the full response for debugging
        if (data && !data.user) {
          console.error('Full server response:', data);
        }
      } finally {
        toggle();
      }
    },
  });
};

export const useSignin = (toast, toggle) => {
  return useMutation({
    mutationFn: async (formData) => {
      toggle();
      const { data } = await axios.post(`${API_URI}/auth/login`, formData);
      localStorage.setItem("user", JSON.stringify(data));

      return data;
    },
    onError: (error) => {
      toggle();
      toast.error(error?.response?.data?.message ?? error.message);
    },
    onSuccess: (data) => {
      toggle();
      toast.success(data?.message);

      setTimeout(() => {
        window.location.replace("/");
      }, 1000);
    },
  });
};

export const useVerification = (toast) => {
  return useMutation({
    mutationFn: async ({ id, otp }) => {
      const { data } = await axios.post(`${API_URI}/users/verify/${id}/${otp}`);
      return data;
    },
    onError: (error, data) => {
      toast.error(error?.response?.data?.message ?? error.message);
    },
    onSuccess: (data) => {
      toast.success(data?.message);

      setTimeout(() => {
        localStorage.removeItem("otp_data");
        window.location.replace("/auth");
      }, 1000);
    },
  });
};

export const useResend = (toast) => {
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.post(`${API_URI}/users/resend-link/${id}`);

      return data;
    },
    onError: (error, data) => {
      toast.error(error?.response?.data?.message ?? error.message);
    },
    onSuccess: (data) => {
      toast.success(data?.message);
      localStorage.setItem(
        "otp_data",
        JSON.stringify({
          otpLevel: true,
          id: data.user._id,
        })
      );

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
  });
};

export const useUpdateUser = (toast, token) => {
  return useMutation({
    mutationFn: async (formData) => {
      const { data } = await axios.put(
        `${API_URI}/users/update-user/`,
        formData,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        }
      );
      localStorage.setItem("user", JSON.stringify(data));

      return data;
    },
    onError: (error, data) => {
      toast.error(error?.response?.data?.message ?? error.message);
    },
    onSuccess: (data) => {
      toast.success(data?.message);

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
  });
};
